import { useLocale } from '../../application/i18n/LocaleProvider';
import { InstallIcon } from '../icons/ControlIcons';

/**
 * The offer to keep the app (SPEC-016).
 *
 * NON-BLOCKING BY CONSTRUCTION, and the constraint is structural rather than a
 * promise in a comment: it is a `<section>` in the document flow, not a
 * `<dialog>`. It has no backdrop, traps no focus, and cannot be the thing
 * between a visitor and the catalogue. `autoUpdatePolicy.js` argues against
 * spending a visitor's attention on a decision that is not theirs to make —
 * this one IS theirs, which is the only reason it is allowed to ask at all, and
 * it still asks from the bottom of the page rather than over the top of it.
 *
 * The instruction under the heading is the load-bearing part. Only Chromium
 * hands the page a prompt to fire; on iOS Safari, on desktop Safari and on
 * Firefox the primary action can do no more than repeat the sentence, so the
 * sentence has to be there.
 */

/** The three claims, each of which is true — which is the only reason it is here. */
const BENEFIT_KEYS = [
    'pwa.install.benefit.fast',
    'pwa.install.benefit.offline',
    'pwa.install.benefit.notify',
];

export default function InstallOfferCard({ actionKey, howToKey, onInstall, onDismiss }) {
    const { t } = useLocale();

    return (
        <section className="app-offer" aria-labelledby="install-offer-title">
            <span className="app-offer__mark" aria-hidden="true">
                <InstallIcon size={26} />
            </span>

            <h2 className="app-offer__title" id="install-offer-title">{t('pwa.install.title')}</h2>
            <p className="app-offer__body">{t('pwa.install.body')}</p>

            <ul className="app-offer__benefits">
                {BENEFIT_KEYS.map((benefitKey) => (
                    <li className="app-offer__benefit" key={benefitKey}>{t(benefitKey)}</li>
                ))}
            </ul>

            {/* Where the browser gives us no prompt to call, this line IS the
                answer. Hiding it behind the button would leave those visitors
                pressing a control that cannot install anything. */}
            {howToKey ? <p className="app-offer__how">{t(howToKey)}</p> : null}

            <div className="app-offer__actions">
                {actionKey ? (
                    <button type="button" className="button button--primary" onClick={onInstall}>
                        {t(actionKey)}
                    </button>
                ) : null}
                <button type="button" className="app-offer__dismiss" onClick={onDismiss}>
                    {t('pwa.install.dismiss')}
                </button>
            </div>
        </section>
    );
}
